import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { EmployeeForm } from '../../components/EmployeeForm/EmployeeForm'
import { MemberFormPageLayout } from '../../components/EmployeeForm/MemberFormPageLayout'
import {
  useEditUserMutation,
  useGetUserByIdQuery,
} from '../../redux/user.endpoints'
import { EditUserDto } from '../../types/User'
import { BackendError } from '../../types/Errors'
import { useLazyGetUserTechStackByUserIdQuery } from '../../redux/techstack.endpoints'
import { TechStackList } from '../../types/TechStacks'
import { useMemberFormData } from '../../hooks/useMemberFormData'

interface UserTechStack {
  technology: { id: number }
  experienceLevel: { id: number }
}

interface Props {
  companyId: number
}

const EditEmployee = ({ companyId }: Props) => {
  const navigate = useNavigate()
  const { userId } = useParams()
  const mainRef = useRef<HTMLElement>(null)
  const [editUser, editUserResult] = useEditUserMutation()
  const { data: user, isSuccess } = useGetUserByIdQuery(userId || '', {
    skip: !userId,
  })
  const { tribes, teams, technologyList, experienceLevel, isReady } =
    useMemberFormData(companyId)
  const [getUserTechStackByUserId, userTechStackResponse] =
    useLazyGetUserTechStackByUserIdQuery()
  const [selectedTechStackList, setSelectedTechStackList] = useState<
    TechStackList[]
  >([])

  useEffect(() => {
    if (!userId) {
      navigate('/employees')
    } else {
      getUserTechStackByUserId({ userId })
    }
  }, [getUserTechStackByUserId, navigate, userId])

  useEffect(() => {
    if (editUserResult && editUserResult.isError) {
      const editUserError = editUserResult.error as BackendError
      if (editUserError.data.error) {
        toast.error(editUserError.data.error, {
          toastId: 'edit-user-error',
        })
      } else {
        toast.error(`An error occurred when updating member.`, {
          toastId: 'edit-user-error',
        })
      }
    } else if (editUserResult && editUserResult.isSuccess) {
      toast.success(`Member updated successfully!`, {
        toastId: 'edit-user-success',
      })
    }
  }, [editUserResult])

  const handleEditUser = async (args: EditUserDto) => {
    const result = await editUser(args)
    if (!('error' in result)) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    if (userTechStackResponse.data) {
      const selectedTechStackOfUser = (
        userTechStackResponse.data as UserTechStack[]
      ).map(techStack => ({
        idTechnology: techStack.technology.id,
        idExperienceLevel: techStack.experienceLevel.id,
      }))
      setSelectedTechStackList(selectedTechStackOfUser)
    }
  }, [userTechStackResponse.data])

  return (
    <MemberFormPageLayout
      ref={mainRef}
      title="Edit Member"
      isReady={isReady && isSuccess}
    >
      <EmployeeForm
        handleEditSubmit={handleEditUser}
        displayErrorBanner={editUserResult.isError}
        loading={editUserResult.isLoading}
        companyId={companyId}
        user={user}
        tribes={tribes}
        teams={teams}
        technologyList={technologyList}
        experienceLevel={experienceLevel}
        selectedTechStackListByUser={selectedTechStackList}
      />
    </MemberFormPageLayout>
  )
}

export default EditEmployee
